import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { resolveReveal } from "@/lib/reveal";
import { playBuildup, playTick, playFanfare, stopAll } from "@/lib/sound";

// Fires theme-specific confetti/effects through a provided fire() instance
function playThemeEffects(fire, theme, color) {
  const colors = [color, "#ffffff", "#EAB308"];
  if (theme === "fireworks") {
    const end = Date.now() + 3500;
    const shoot = () => {
      fire({ particleCount: 40, spread: 360, startVelocity: 35, origin: { x: Math.random(), y: Math.random() * 0.5 }, colors: [color, "#EAB308", "#EC4899", "#3B82F6", "#22C55E"] });
      if (Date.now() < end) setTimeout(shoot, 350);
    };
    shoot();
  } else if (theme === "balloons") {
    fire({ particleCount: 120, spread: 100, origin: { y: 0.6 }, colors });
  } else if (theme === "neon") {
    const end = Date.now() + 2800;
    const frame = () => {
      fire({ particleCount: 8, angle: 90, spread: 120, startVelocity: 45, origin: { x: Math.random(), y: 1 }, colors: ["#22D3EE", "#A855F7", "#EC4899", color], shapes: ["star"], scalar: 1.2 });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  } else {
    // classic
    const end = Date.now() + 2500;
    (function frame() {
      fire({ particleCount: 6, angle: 60, spread: 70, origin: { x: 0 }, colors });
      fire({ particleCount: 6, angle: 120, spread: 70, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    fire({ particleCount: 180, spread: 120, origin: { y: 0.5 }, colors });
  }
}

export default function RevealAnimation({ event, onDone }) {
  const [phase, setPhase] = useState("build");
  const [count, setCount] = useState(3);
  const { headline, color } = resolveReveal(event);
  const theme = event.animation_theme || "classic";
  const firedRef = useRef(false);
  const canvasRef = useRef(null);
  const fireRef = useRef(null);

  useEffect(() => {
    if (canvasRef.current && !fireRef.current) {
      // Dedicated canvas on the main thread (useWorker:false) — the default worker/
      // OffscreenCanvas path is unreliable on iOS Safari and leaves particles static.
      fireRef.current = confetti.create(canvasRef.current, { resize: true, useWorker: false, disableForReducedMotion: false });
    }
    return () => { if (fireRef.current) { try { fireRef.current.reset(); } catch (e) { console.debug(e); } } };
  }, []);

  useEffect(() => {
    playBuildup(2.4);
    const t1 = setTimeout(() => setPhase("count"), 1600);
    return () => { clearTimeout(t1); stopAll(); };
  }, []);

  useEffect(() => {
    if (phase !== "count") return;
    if (count <= 0) { setPhase("boom"); return; }
    playTick(count);
    const t = setTimeout(() => setCount((c) => c - 1), 900);
    return () => clearTimeout(t);
  }, [phase, count]);

  useEffect(() => {
    if (phase !== "boom" || firedRef.current) return;
    firedRef.current = true;
    playFanfare();
    playThemeEffects(fireRef.current || confetti, theme, color);
    if (onDone) setTimeout(onDone, 4000);
  }, [phase, color, theme, onDone]);

  const isBoom = phase === "boom";
  const bg = isBoom ? "rgba(5,5,5,0)" : "rgba(5,5,5,1)";

  return (
    <motion.div
      className={`fixed inset-0 z-[100] flex overflow-hidden pointer-events-none ${isBoom ? "items-end justify-center" : "items-center justify-center"}`}
      initial={{ backgroundColor: "rgba(5,5,5,1)" }}
      animate={{ backgroundColor: bg }}
      transition={{ duration: 0.5 }}
      data-testid="reveal-animation-overlay"
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }} />
      {/* neon glow rings */}
      {phase === "boom" && theme === "neon" && (
        <>
          <motion.div className="absolute rounded-full border-2" style={{ borderColor: color, width: 300, height: 300 }}
            initial={{ scale: 0, opacity: 0.8 }} animate={{ scale: 6, opacity: 0 }} transition={{ duration: 2, repeat: Infinity }} />
          <motion.div className="absolute rounded-full border-2 border-cyan-400" style={{ width: 300, height: 300 }}
            initial={{ scale: 0, opacity: 0.8 }} animate={{ scale: 6, opacity: 0 }} transition={{ duration: 2, repeat: Infinity, delay: 0.6 }} />
        </>
      )}
      {/* balloons rising */}
      {phase === "boom" && theme === "balloons" && (
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 14 }).map((_, i) => (
            <motion.div key={`balloon-${i}`} className="absolute rounded-full"
              style={{ left: `${(i * 7 + 5) % 100}%`, width: 42, height: 54, background: [color, "#EAB308", "#ffffff", "#EC4899"][i % 4], opacity: 0.9 }}
              initial={{ y: "110vh" }} animate={{ y: "-20vh" }} transition={{ duration: 5 + (i % 4), delay: (i % 5) * 0.2, ease: "easeOut" }} />
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {phase === "build" && (
          <motion.div key="build" className="text-center" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: [1, 1.12, 1], opacity: 1 }} exit={{ opacity: 0 }} transition={{ scale: { repeat: Infinity, duration: 0.7 } }}>
            <p className="font-impact text-5xl sm:text-7xl text-white/90">Are you ready?</p>
            <p className="mt-4 text-muted-foreground tracking-[0.3em] uppercase text-sm">Hold your breath…</p>
          </motion.div>
        )}
        {phase === "count" && count > 0 && (
          <motion.div key={`c-${count}`} initial={{ scale: 2.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.2, opacity: 0 }} className="font-impact text-white" style={{ fontSize: "min(45vw, 22rem)", lineHeight: 1 }}>
            {count}
          </motion.div>
        )}
        {phase === "boom" && (
          <motion.div key="boom" data-testid="reveal-result" className="w-full px-3 pb-6 sm:pb-10 relative z-10 flex justify-center" initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 18 }}>
            <div className="w-full max-w-3xl rounded-3xl px-5 py-5 sm:px-8 sm:py-7 text-center" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.4))", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
              <h1 className="font-display font-black leading-[1.08] tracking-tight break-words [overflow-wrap:anywhere]" style={{ fontSize: "clamp(1.75rem, 9vw, 5.5rem)", color: color, textShadow: theme === "neon" ? `0 0 30px ${color}` : "0 3px 18px rgba(0,0,0,0.85), 0 6px 30px rgba(0,0,0,0.55)" }}>
                {headline}
              </h1>
              {event.message ? (
                <motion.p data-testid="reveal-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-3 sm:mt-4 text-white/90 text-base sm:text-xl leading-relaxed max-w-2xl mx-auto break-words [overflow-wrap:anywhere]">
                  {event.message}
                </motion.p>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
